import { MaterialCommunityIcons } from "@expo/vector-icons";
import React, { createContext, useContext, useState } from "react";
import { Platform, Pressable, StyleSheet, Text, View, } from "react-native";
import { useCustomTheme } from "../hooks/useCustomTheme";
const TableContext = createContext([]);
function getTotalColumnWidth(columns) {
    const total = columns.reduce((sum, column) => sum + column.width, 0);
    return total > 0 ? total : 1;
}
function getColumnWidthStyle(columnWidth, totalWidth) {
    const widthPercent = `${(columnWidth / totalWidth) * 100}%`;
    return {
        width: widthPercent,
        maxWidth: widthPercent,
        flexBasis: widthPercent,
        flexGrow: 0,
        flexShrink: 0,
    };
}
const ThemedTableRow = ({ columns, isFirst = false, isLast = false, }) => {
    const { colors, fonts } = useCustomTheme();
    const headerColumns = useContext(TableContext);
    const totalColumnWidth = getTotalColumnWidth(headerColumns);
    const [isHovered, setIsHovered] = useState(false);
    const [hoveredActionKey, setHoveredActionKey] = useState(null);
    const [focusedActionKey, setFocusedActionKey] = useState(null);
    if (__DEV__) {
        validateColumns(columns, headerColumns);
    }
    const dynamicRowStyles = {
        borderColor: colors.border,
        borderTopWidth: isFirst ? 0 : 1,
        borderBottomWidth: isLast ? 1 : 0,
        borderBottomLeftRadius: isLast ? 8 : 0,
        borderBottomRightRadius: isLast ? 8 : 0,
        backgroundColor: isHovered ? colors.tableRowHover : undefined,
    };
    const hoverHandlers = Platform.OS === "web"
        ? {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
        }
        : undefined;
    return (<View style={[styles.rowContainer, dynamicRowStyles]} {...hoverHandlers}>
      {columns.map((column, index) => {
            const width = headerColumns[index]?.width || 1;
            const columnStyle = headerColumns[index]?.style;
            const columnWidthStyle = getColumnWidthStyle(width, totalColumnWidth);
            if (column.type === "text") {
                return (<View key={`text-${index}`} style={[
                        styles.columnContainer,
                        columnWidthStyle,
                        styles.cellContainer,
                    ]}>
              {isMultiTextColumn(column) ? (<View style={styles.cellTextContainer}>
                  {column.parts.map((part) => (<Text key={part.label} style={[
                                styles.cellText,
                                styles.paddingLeft,
                                fonts.regular,
                                columnStyle,
                                { color: colors.text },
                                part.style,
                            ]}>
                      {part.label}
                    </Text>))}
                </View>) : (<Text style={[
                            styles.cellText,
                            styles.paddingLeft,
                            fonts.regular,
                            columnStyle,
                            { color: colors.text },
                            column.style,
                        ]}>
                  {column.label}
                </Text>)}
            </View>);
            }
            else if (column.type === "action") {
                return (<View key={`action-${index}`} style={[
                        styles.columnContainer,
                        styles.actionContainer,
                        styles.paddingLeft,
                        columnWidthStyle,
                    ]}>
              {column.actions.map((action, actionIndex) => {
                        const actionKey = `${index}-${actionIndex}`;
                        const isDisabled = Boolean(action.disabled);
                        const borderColor = isDisabled
                            ? "transparent"
                            : focusedActionKey === actionKey
                                ? colors.palette.spinach30
                                : hoveredActionKey === actionKey
                                    ? colors.text
                                    : "transparent";
                        return (<Pressable key={`${action.label}-${actionIndex}`} disabled={isDisabled} focusable={!isDisabled} accessibilityState={{ disabled: isDisabled }} onHoverIn={() => {
                                if (isDisabled)
                                    return;
                                setHoveredActionKey(actionKey);
                            }} onHoverOut={() => {
                                setHoveredActionKey((current) => current === actionKey ? null : current);
                            }} onFocus={() => {
                                if (isDisabled)
                                    return;
                                setFocusedActionKey(actionKey);
                            }} onBlur={() => {
                                setFocusedActionKey((current) => current === actionKey ? null : current);
                            }} onPress={action.onPress} style={[
                                styles.actionButton,
                                isDisabled && styles.actionButtonDisabled,
                                Platform.OS === "web"
                                    ? { outlineStyle: "none" }
                                    : undefined,
                                { borderColor },
                            ]}>
                    {action.icon && (<MaterialCommunityIcons name={action.icon} size={20}/>)}
                    <Text style={[styles.actionText, fonts.bold]}>
                      {action.label}
                    </Text>
                  </Pressable>);
                    })}
            </View>);
            }
            return null;
        })}
    </View>);
};
function isMultiTextColumn(column) {
    return "parts" in column && Array.isArray(column.parts);
}
function validateColumns(rowColumns, headerColumns) {
    if (rowColumns.length !== headerColumns.length) {
        console.warn(`Mismatch in number of columns: row has ${rowColumns.length}, header has ${headerColumns.length}`);
    }
}
export const ThemedTable = ({ headerColumns, children, }) => {
    const { colors } = useCustomTheme();
    const childrenArray = React.Children.toArray(children);
    return (<TableContext.Provider value={headerColumns}>
      <View style={[
            styles.tableContainer,
            {
                borderColor: colors.border,
            },
        ]}>
        <TableHeader columns={headerColumns}/>
        {childrenArray.map((child, index, arr) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, {
                    ...child.props,
                    isFirst: index === 0,
                    isLast: index === arr.length - 1,
                    key: child.key || index,
                });
            }
            return child;
        })}
      </View>
    </TableContext.Provider>);
};
function TableHeader({ columns }) {
    const { colors } = useCustomTheme();
    const totalColumnWidth = getTotalColumnWidth(columns);
    return (<View style={[styles.headerContainer, { backgroundColor: colors.border }]}>
      {columns.map((headerColumn) => {
            const key = headerColumn.label ||
                headerColumn.parts.map((part) => part.label).join("-");
            const columnWidthStyle = getColumnWidthStyle(headerColumn.width, totalColumnWidth);
            return (<View key={key} style={[styles.columnContainer, columnWidthStyle]}>
            {"parts" in headerColumn && headerColumn.parts ? (<View style={[styles.headerColumnContainer, styles.paddingLeft]}>
                {headerColumn.parts.map((part) => (<Text key={part.label} style={[
                            styles.defaultHeaderTextStyle,
                            { color: colors.background },
                            part.style,
                        ]}>
                    {part.label}
                  </Text>))}
              </View>) : (<Text style={[
                        styles.defaultHeaderTextStyle,
                        styles.paddingLeft,
                        { color: colors.background },
                        headerColumn.style,
                    ]}>
                {headerColumn.label}
              </Text>)}
          </View>);
        })}
    </View>);
}
ThemedTable.Row = ThemedTableRow;
const styles = StyleSheet.create({
    tableContainer: {
        width: "100%",
        borderRadius: 8,
        borderLeftWidth: 1,
        borderRightWidth: 1,
    },
    headerContainer: {
        flexDirection: "row",
        height: 58,
        alignItems: "center",
        borderTopLeftRadius: 8,
        borderTopRightRadius: 8,
    },
    defaultHeaderTextStyle: {
        fontFamily: "PoppinsRegular",
        fontSize: 15,
        lineHeight: 18,
    },
    columnContainer: {
        minWidth: 0,
        overflow: "hidden",
    },
    rowContainer: {
        flexDirection: "row",
        minHeight: 91,
        alignItems: "center",
        paddingVertical: 8,
    },
    cellText: {
        fontSize: 16,
        lineHeight: 20.8,
        flexShrink: 1,
        // CSS properties for intelligent word breaking (Expo Web only)
        ...(Platform.OS === "web" && {
            wordBreak: "break-word",
            hyphens: "auto",
            overflowWrap: "break-word",
        }),
    },
    cellContainer: {
        justifyContent: "center",
    },
    cellTextContainer: {
        flexDirection: "column",
    },
    actionContainer: {
        gap: 8,
        justifyContent: "center",
        alignItems: "flex-start",
    },
    actionButton: {
        flexDirection: "row",
        gap: 8,
        alignItems: "center",
        borderWidth: 2,
        borderRadius: 4,
        alignSelf: "flex-start",
        paddingHorizontal: 4,
    },
    actionButtonDisabled: {
        opacity: 0.5,
    },
    actionText: {
        fontSize: 16,
        lineHeight: 20.8,
    },
    paddingLeft: {
        paddingLeft: 16,
    },
    headerColumnContainer: {
        width: "100%",
    },
});
//# sourceMappingURL=ThemedTable.js.map